import { defineComponent as _defineComponent } from 'vue'
import { HtmlHTMLAttributes, InputHTMLAttributes, computed, nextTick, ref, useSlots } from 'vue';
import { debounce } from 'lodash';
import CError from '@/components/shared/CError.vue';

type InputValue = string | number;
type InputSize = 'lg' | 'md' | 'sm' | 'xs';
type InputType = 'text' | 'search' | 'number' | 'decimal' | 'email';


export default /*#__PURE__*/_defineComponent({
  __name: 'CInput',
  props: {
    value: null,
    id: null,
    inputClass: null,
    type: { default: 'search' },
    disabled: { type: Boolean },
    required: { type: Boolean },
    minlength: null,
    maxlength: null,
    min: null,
    max: null,
    step: null,
    fractionDigits: { default: 2 },
    placeholder: null,
    label: null,
    labelClass: null,
    inline: { type: Boolean },
    inputSize: { default: 'md' },
    error: { type: Boolean },
    errorMsg: null,
    debounce: { default: 1000 },
    pattern: null
  },
  emits: ["input", "blur", "enter", "debounce", "keyup"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'input', value: InputValue): void;
    (event: 'blur', value: InputValue): void;
    (event: 'enter', value: InputValue): void;
    (event: 'debounce', value: InputValue): void;
    (event: 'keyup', value: KeyboardEvent): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value?: InputValue, id?: InputHTMLAttributes['id'], inputClass?: InputHTMLAttributes['class'], type: InputType, disabled?: boolean, required?: boolean, minlength?: InputHTMLAttributes['minlength'], maxlength?: InputHTMLAttributes['maxlength'], min?: number, max?: number, step?: InputHTMLAttributes['step'], fractionDigits: number, placeholder?: HtmlHTMLAttributes['placeholder'], label?: string, labelClass?: HtmlHTMLAttributes['class'], inline?: boolean, inputSize: InputSize, error?: boolean, errorMsg?: string, debounce: number, pattern?: RegExp };





const onKeyup = (e: KeyboardEvent) => {
    if (e.code == 'Enter' || e.key == 'Enter') {
        emit('enter', model.value);
    }
    emit('keyup', e);
};

const slot = useSlots();

const computedClass = computed(() => {
    const classes = [props.inputClass, `form-control-${props.inputSize}`];

    if (props.error) {
        classes.push('is-invalid');
    }

    return classes;
});

const computedType = computed(() => {
    const proxyTypes: Record<string, string> = {
        decimal: 'number'
    };

    return proxyTypes[props.type] || props.type;
});

const inputRef = ref<HTMLInputElement | undefined>();
const model = computed({
    get: () => props.value || '',
    set: (value: InputValue | undefined) => {
        const _value = validateInputValue(value);
        emit('input', _value);
        nextTick(() => {
            if (!inputRef.value || _value == value) return;
            inputRef.value.value = String(_value);
        });
    }
});

const validateInputValue = (value: InputValue | undefined): InputValue => {
    let newVal = value;

    if (!['number', 'decimal'].includes(props.type)) return newVal || '';

    if (value == undefined && !props.min) return newVal || 0;

    newVal = Number(value);

    if (props.type == 'decimal') {
        const decimal = newVal.toFixed(props.fractionDigits);
        newVal = parseFloat(decimal);
    }

    if (props.min != undefined && newVal < props.min) {
        newVal = props.min;
    } else if (props.max != undefined && newVal > props.max) {
        newVal = props.max;
    }

    return newVal;
};

const triggerDebounce = debounce(() => {
    emit('debounce', model.value);
}, props.debounce);

return { __sfc: true,props, emit, onKeyup, slot, computedClass, computedType, inputRef, model, validateInputValue, triggerDebounce, CError }
}

})